/**
 * PLATFORM_ADMIN read-only org viewer: files the request document that
 * functions/src/platformAdmin/platformAdminHandlers.ts acts on. The claims and
 * the derived profile are in platformAdminState.ts.
 */
import { addDoc, collection, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';

export type PlatformAdminOutcome =
    | { status: 'done'; orgs?: { id: string; name: string }[]; orgId?: string; orgName?: string; expiresAt?: number }
    | { status: 'refused'; reason: string };

/**
 * Full reload to the app root after a view starts or ends. A reload, not a
 * router navigation: every listener then opens on the refreshed token, and no
 * state or cached documents from the previous org survive into the next.
 */
export function reloadToHome(): void {
    window.location.assign('/');
}

/**
 * File one request and wait for the trigger's outcome. After a start or end
 * that succeeded, force an ID token refresh so the new claims reach the rules.
 */
export async function requestPlatformAdminAction(
    action: 'list' | 'start' | 'end',
    orgId: string | null = null,
    purpose?: string,
    timeoutMs = 30_000,
): Promise<PlatformAdminOutcome> {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const ref = await addDoc(collection(db, 'platformAdminRequests'), {
        uid: user.uid,
        action,
        orgId,
        status: 'pending',
        createdAt: serverTimestamp(),
        // Only a start states a purpose; firestore.rules refuses one on list or end.
        ...(action === 'start' ? { purpose: (purpose ?? '').trim() } : {}),
    });
    const outcome = await new Promise<PlatformAdminOutcome>((resolve, reject) => {
        let unsubscribe = () => {};
        const timer = setTimeout(() => {
            unsubscribe();
            reject(new Error('The request timed out. Try again.'));
        }, timeoutMs);
        unsubscribe = onSnapshot(
            ref,
            (snap) => {
                const data = snap.data();
                if (data && data.status !== 'pending') {
                    clearTimeout(timer);
                    unsubscribe();
                    resolve(data as PlatformAdminOutcome);
                }
            },
            (err) => {
                clearTimeout(timer);
                unsubscribe();
                reject(err);
            },
        );
    });
    if (outcome.status === 'done' && action !== 'list') {
        await user.getIdToken(true);
    }
    return outcome;
}
